/**
 * pwaInstall — lógica PURA do banner "Adicionar à tela inicial" (Story X1.1.3).
 *
 * Zero dependência de React/DOM → testável com vitest sem mocks (mesmo padrão
 * de lib/timeTracking.ts e lib/gantt.ts). O componente InstallBanner faz o wire
 * com os event listeners (`beforeinstallprompt`/`appinstalled`), `matchMedia`,
 * `navigator.userAgent` e `localStorage`; aqui ficam só as decisões.
 *
 * Decisões da story (3 opt × 3, todas recomendadas):
 * - iOS Safari NÃO dispara `beforeinstallprompt` → fallback com instruções
 *   manuais (detecção via UA). [[project_story_e11_2_4_2026-05-29]] usou o mesmo
 *   estilo de helper puro.
 * - "Track install" sem sink de eventos genérico: `appinstalled` grava flag em
 *   localStorage (suprime o banner pra sempre) + log; sem endpoint fake.
 * - Dismiss persiste 7 dias via timestamp em localStorage.
 */

export const PWA_DISMISS_KEY = "seweb:pwa:install-dismissed-until";
export const PWA_INSTALLED_KEY = "seweb:pwa:installed";
export const PWA_DISMISS_DAYS = 7;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Interface mínima de storage (localStorage em prod; objeto fake nos testes). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** True ⇔ UA é um device iOS (iPhone/iPad/iPod). iPadOS 13+ se disfarça de Mac. */
export function isIosDevice(userAgent: string): boolean {
  if (/iphone|ipad|ipod/i.test(userAgent)) return true;
  // iPadOS 13+ reporta UA de Mac; distingue por touch (checado no componente).
  return false;
}

/**
 * True ⇔ o app está aberto como app instalado AGORA (display-mode standalone ou o flag
 * legado do iOS). Não confundir com `isInstalled(store)`: aquele é a flag persistida de que
 * o `appinstalled` já disparou alguma vez. Compartilhada pelo banner e pelo cartão do push
 * (passo 8); lê `window`, por isso fica fora da parte pura acima.
 */
export function estaInstalado(): boolean {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia?.("(display-mode: standalone)");
  // `navigator.standalone` é o flag legado do iOS Safari.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return Boolean(mql?.matches) || iosStandalone === true;
}

/** True ⇔ este aparelho é iPhone/iPad/iPod, inclusive o iPad que se disfarça de Mac (toque). */
export function ehIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (isIosDevice(ua)) return true;
  // iPadOS 13+ se disfarça de Mac — distingue por touch.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

/** Grava o timestamp (now + dias) até o qual o banner fica oculto. */
export function setDismissed(
  store: KeyValueStore,
  now: number,
  days: number = PWA_DISMISS_DAYS
): void {
  store.setItem(PWA_DISMISS_KEY, String(now + days * MS_PER_DAY));
}

/** True ⇔ ainda estamos dentro da janela de dismiss (banner deve ficar oculto). */
export function isDismissActive(store: KeyValueStore, now: number): boolean {
  const raw = store.getItem(PWA_DISMISS_KEY);
  if (!raw) return false;
  const until = Number(raw);
  // Valor corrompido (NaN) → trata como não-dismissado (fail-open pro banner).
  if (!Number.isFinite(until)) return false;
  return now < until;
}

/** Marca como instalado (suprime o banner permanentemente). */
export function markInstalled(store: KeyValueStore): void {
  store.setItem(PWA_INSTALLED_KEY, "true");
}

/** True ⇔ já foi instalado em algum momento (flag persistida). */
export function isInstalled(store: KeyValueStore): boolean {
  return store.getItem(PWA_INSTALLED_KEY) === "true";
}

export interface ShouldShowInput {
  /** Viewport mobile (useIsMobile). Banner só em mobile. */
  isMobile: boolean;
  /** App já rodando em modo standalone (instalado/aberto da home screen). */
  isStandalone: boolean;
  /** Flag persistida de instalação (appinstalled já disparou antes). */
  installed: boolean;
  /** Dentro da janela de dismiss de 7d. */
  dismissActive: boolean;
  /** `beforeinstallprompt` já disparou e temos o evento guardado (Chromium). */
  hasNativePrompt: boolean;
  /** Device iOS (sem beforeinstallprompt → fallback de instruções manuais). */
  isIos: boolean;
}

/**
 * Decisão central: o banner deve aparecer?
 *
 * Suprime quando: não-mobile, já standalone, já instalado, ou dismiss ativo.
 * Caso contrário, mostra se há prompt nativo (Chromium) OU é iOS (fallback
 * manual). Em desktop ou browser sem suporte e sem iOS, fica oculto.
 */
export function shouldShowBanner(input: ShouldShowInput): boolean {
  const { isMobile, isStandalone, installed, dismissActive, hasNativePrompt, isIos } = input;
  if (!isMobile || isStandalone || installed || dismissActive) return false;
  return hasNativePrompt || isIos;
}

/** Modo de exibição do banner — define o CTA/instruções renderizados. */
export type InstallBannerMode = "native" | "ios";

/** Qual variante renderizar quando o banner aparece. */
export function bannerMode(hasNativePrompt: boolean): InstallBannerMode {
  return hasNativePrompt ? "native" : "ios";
}
